import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { api } from "../api/client";
import { Icon } from "../components/Icon";
import { ErrorBox, Spinner } from "../components/Spinner";
import { RichDiffView, type RichDiffSource, type RichDiffTab } from "../richdiff/RichDiffView";

/** The rich diff of a task's change, opened in its own window from the review. */
export function RichDiffPage() {
  const { tid = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as RichDiffTab) || "trace";
  const qc = useQueryClient();
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["rich-diff", tid],
    queryFn: () => api.richDiff(tid),
    staleTime: 60_000,
  });
  const source: RichDiffSource = useMemo(
    () => ({
      loadFile: (sha, path) => api.richDiffFile(tid, sha, path).then((f) => f.content, () => null),
      loadPatch: () => api.diff(tid).then((d) => d?.diff ?? null),
    }),
    [tid],
  );

  useEffect(() => {
    if (data) document.title = `#${data.task.number} rich diff · ${data.task.title}`;
  }, [data]);

  if (isLoading) return <Spinner label="Reading the change…" />;
  if (error || !data) {
    return (
      <div className="page page-narrow">
        <ErrorBox error={error ?? new Error("Not found")} />
      </div>
    );
  }
  const title = (
    <>
      <span className="muted">#{data.task.number}</span> {data.task.title}
    </>
  );
  const openTask = (
    <Link className="btn btn-sm" to={`/p/${data.task.project_id}/tasks/${data.task.id}`}>
      Open task
    </Link>
  );
  if (!data.analysis) {
    return (
      <div className="page page-narrow">
        <h1 className="page-title">{title}</h1>
        <p className="muted">{data.missing}</p>
        <p className="muted small">
          A project describes its traceability and database schema to the rich diff in a <code className="code-inline">rich-diff.toml</code> at
          the root of its repository; see <code className="code-inline">crates/rich-diff/README.md</code> in the agent task runner.
        </p>
        {openTask}
      </div>
    );
  }
  return (
    <RichDiffView
      analysis={data.analysis}
      source={source}
      title={title}
      tab={tab}
      onTab={(t) =>
        setParams((p) => {
          p.set("tab", t);
          return p;
        })
      }
      actions={
        <>
          <button
            className="btn btn-sm"
            onClick={() => qc.invalidateQueries({ queryKey: ["rich-diff", tid] })}
            disabled={isFetching}
            title="Fetch the latest pushed changes"
          >
            <Icon name="refresh" size={13} /> {isFetching ? "Refreshing…" : "Refresh"}
          </button>
          {openTask}
        </>
      }
    />
  );
}
